import { useState, useEffect } from "react";
import themeData from "../utils/theme.json";
import { environment } from "src/config/environment";

// export const pxToRem = (px: number): string => `${px / 12}rem`;

export const getAntdThemeFromCSSVariables = () => {
	const root = document.documentElement;

	return {
		colorPrimary: getComputedStyle(root).getPropertyValue("--primary-color").trim(),
		colorLink: getComputedStyle(root).getPropertyValue("--link-color").trim(),
		colorTextBase: getComputedStyle(root).getPropertyValue("--text-base-color").trim(),
		colorBgBase: getComputedStyle(root).getPropertyValue("--bg-base-color").trim(),
		// fontSize: pxToRem(parseInt(getComputedStyle(root).getPropertyValue("--font-size").trim())),
	};
};

export const useTheme = () => {
	const [theme, setTheme] = useState(getAntdThemeFromCSSVariables());

	const updateTheme = () => {
		setTheme(getAntdThemeFromCSSVariables());
	};
	useEffect(() => {
		updateTheme();

		// Set up a MutationObserver to watch for changes in CSS variables
		const observer = new MutationObserver(updateTheme);
		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ["style"],
		});

		// Clean up the observer when the component unmounts
		return () => observer.disconnect();
	}, []);

	return theme;
};

export const adjustColor = (hex: string, percent: number): string => {
	hex = hex.replace("#", "");

	let r = parseInt(hex.substring(0, 2), 16);
	let g = parseInt(hex.substring(2, 4), 16);
	let b = parseInt(hex.substring(4, 6), 16);

	const adjustChannel = (channel: number): number => {
		if (percent > 0) {
			return Math.max(0, Math.min(255, Math.floor(channel + (255 - channel) * (percent / 100))));
		} else {
			return Math.max(0, Math.min(255, Math.floor(channel * (1 + percent / 100))));
		}
	};

	r = adjustChannel(r);
	g = adjustChannel(g);
	b = adjustChannel(b);

	const toHex = (value: number) => value.toString(16).padStart(2, "0");
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Blend `hex` over background `bg` at the given opacity percent, returning an opaque hex.
// Used for sticky tables / overlapping elements where a translucent tint would let
// underlying content bleed through. channel = round(color·a + bg·(1−a)).
export const blendOver = (hex: string, bg: string, percent: number): string => {
	const a = percent / 100;
	const h = hex.replace("#", "");
	const b = bg.replace("#", "");
	const mix = (i: number) => {
		const c = parseInt(h.substring(i, i + 2), 16);
		const bgc = parseInt(b.substring(i, i + 2), 16);
		return Math.round(c * a + bgc * (1 - a))
			.toString(16)
			.padStart(2, "0");
	};
	return `#${mix(0)}${mix(2)}${mix(4)}`;
};

export const getThemeNames = (): string[] => {
	return Object.keys(themeData.colors);
};

export const getThemeName = (): string => {
	return environment.REACT_APP_THEME_NAME ?? "Kural";
};

const OPACITY_LEVELS = Array.from({ length: 19 }, (_, i) => (i + 1) * 5).concat(3, 8);
const SHADE_KEYS = ["primary-color", "primary-color-2", "secondary-color", "secondary-color-2", "neutral-grey"];

// Emit the -opacity-N (see-through tint) and -solid-N (same tint blended opaque over
// `bgBase`, for sticky/overlapping elements) ladders for one #RRGGBB color under `name`.
const setOpacityLadder = (root: HTMLElement, name: string, color: string, bgBase: string) => {
	const baseColor = color.slice(0, 7); // First 7 characters (#RRGGBB)
	OPACITY_LEVELS.forEach((opacity) => {
		const hexOpacity = Math.round((opacity * 255) / 100)
			.toString(16)
			.padStart(2, "0");
		root.style.setProperty(`--${name}-opacity-${opacity}`, `${baseColor}${hexOpacity}`);
		root.style.setProperty(`--${name}-solid-${opacity}`, blendOver(baseColor, bgBase, opacity));
	});
};

// Set a color token plus its variant ladders on :root. Shade keys (primary/secondary/
// neutral-grey family) also get -darker/-light/-lighter/-lightest shades, each with its
// own -opacity-N / -solid-N ladder. Shared by loadTheme() (full palette at boot) and
// applyBranding() (BE color overrides at runtime) so a live brand change produces exactly
// the same variant set as the initial load. Non-#hex values (flags, chart-colors array)
// are skipped — chart-colors is consumed directly from theme.json via useChartPalette().
const setColorToken = (root: HTMLElement, key: string, value: string, bgBase: string) => {
	root.style.setProperty(`--${key}`, value);
	if (!value.startsWith("#")) return;

	if (SHADE_KEYS.includes(key)) {
		const darkerShade = adjustColor(value, -20); // Darken by 20%
		const lighterShade = adjustColor(value, 10); // Lighten by 10%
		const lightShade = adjustColor(value, 25); // Lighten by 25%
		const lightestShade = adjustColor(value, 70); // Lighten by 70%

		root.style.setProperty(`--${key}-darker`, darkerShade);
		root.style.setProperty(`--${key}-light`, lightShade);
		root.style.setProperty(`--${key}-lighter`, lighterShade);
		root.style.setProperty(`--${key}-lightest`, lightestShade);

		const shades = [
			{ name: "", color: value },
			{ name: "-darker", color: darkerShade },
			{ name: "-light", color: lightShade },
			{ name: "-lighter", color: lighterShade },
			{ name: "-lightest", color: lightestShade },
		];
		shades.forEach((shade) => setOpacityLadder(root, `${key}${shade.name}`, shade.color, bgBase));
	} else {
		setOpacityLadder(root, key, value, bgBase);
	}
};

// `colorOverrides` is the optional set of BE-driven brand colors (token-keyed,
// e.g. { "primary-color": "#4CBB96" }). When present they layer over the named
// theme's palette — every other token (neutrals, chart-colors, typography,
// radii) keeps inheriting from theme.json. Omit it for the plain theme.json load.
export const loadTheme = (themeName: string, colorOverrides?: Record<string, string>) => {
	const root = document.documentElement;
	const baseColors = themeData.colors[themeName];

	if (!baseColors) {
		console.error(`Theme "${themeName}" not found`);
		return;
	}

	const colors = colorOverrides ? { ...baseColors, ...colorOverrides } : baseColors;

	// Base background that opaque `-solid-N` tints are blended over (white in light themes,
	// the dark base in Kural_Dark_Theme) so sticky/overlap tints stay opaque and re-theme.
	const bgBase = (colors["bg-base-color"] as string) || "#ffffff";

	// Load colors
	Object.entries(colors).forEach(([key, value]: [string, unknown]) => {
		if (typeof value !== "string") return;
		setColorToken(root, key, value, bgBase);
	});

	// Load other properties
	Object.entries(themeData.other[themeName]).forEach(([key, value]: [string, string]) => {
		root.style.setProperty(`--${key}`, value);
	});

	return colors;
};

// Apply just the BE brand-color overrides onto the already-loaded theme, live.
// Setting these CSS vars mutates :root's `style` attribute, which the
// MutationObserver in useTheme() observes to re-derive the antd theme — so antd
// components and any `var(--…)` CSS re-theme with no reload. No-ops on an empty map.
export const applyBranding = (colors: Record<string, string>, themeName: string = getThemeName()) => {
	if (!colors || Object.keys(colors).length === 0) return;

	const root = document.documentElement;
	const bgBase = (themeData.colors[themeName]?.["bg-base-color"] as string) || "#ffffff";
	Object.entries(colors).forEach(([key, value]) => {
		if (typeof value === "string") setColorToken(root, key, value, bgBase);
	});
};
